const RELATED_TABS_COOKIE = 'ff_related_tabs';

// Remembers the last selected facet tab + sub-tab across product pages. The sub-tab is
// stored by its backend Collection handle rather than its position, so it still
// matches when the next product lists its values in a different order.
function readRelatedTabsCookie() {
  const row = document.cookie.split('; ').find((entry) => entry.startsWith(`${RELATED_TABS_COOKIE}=`));
  if (!row) return null;

  try {
    const value = JSON.parse(decodeURIComponent(row.slice(RELATED_TABS_COOKIE.length + 1)));
    return value && typeof value.facet === 'string' ? value : null;
  } catch (error) {
    return null;
  }
}

function writeRelatedTabsCookie(facet, subtab) {
  const value = encodeURIComponent(JSON.stringify({ facet, subtab: subtab || null }));
  document.cookie = `${RELATED_TABS_COOKIE}=${value}; path=/; SameSite=Lax`;
}

if (!customElements.get('related-tabs-panel')) {
  const CARDS_SECTION_ID = 'api-related-cards';

  customElements.define(
    'related-tabs-panel',
    class extends HTMLElement {
      connectedCallback() {
        this.productId = this.dataset.productId;
        this.batchSize = Number(this.dataset.batchSize) || 10;
        this.maxCards = Number(this.dataset.maxCards) || 50;
        this.loadState = new WeakMap();

        this.addEventListener('click', (event) => {
          const subtab = event.target.closest('[data-subtab]');
          if (subtab && this.contains(subtab)) {
            this.selectSubtab(subtab);
            return;
          }

          const seeMore = event.target.closest('[data-see-more]');
          if (seeMore && this.contains(seeMore)) {
            this.loadMore(seeMore);
          }
        });
      }

      selectSubtab(trigger, notify = true) {
        const index = trigger.getAttribute('data-subtab');

        this.querySelectorAll('[data-subtab]').forEach((el) => {
          el.classList.toggle('current', el === trigger);
        });

        this.querySelectorAll('[data-subpanel]').forEach((el) => {
          const isMatch = el.getAttribute('data-subpanel') === index;
          el.classList.toggle('current', isMatch);
          el.hidden = !isMatch;
        });

        if (notify) {
          this.dispatchEvent(new CustomEvent('related-tabs:subtab-change', { bubbles: true }));
        }
      }

      // Per-subpanel paging state, kept across sub-tab switches so loaded cards and
      // the next page to fetch survive switching away and back.
      stateFor(subpanel, grid) {
        let state = this.loadState.get(subpanel);
        if (!state) {
          const seen = new Set();
          grid.querySelectorAll('[data-grid-item]').forEach((card) => {
            const match = (card.id || '').match(/-(\d+)$/);
            if (match) seen.add(match[1]);
          });
          state = { seen, buffer: [], nextPage: 1, pages: Infinity };
          this.loadState.set(subpanel, state);
        }
        return state;
      }

      async loadMore(button) {
        const subpanel = button.closest('[data-subpanel]');
        const grid = subpanel.querySelector('[data-related-grid]');
        const state = this.stateFor(subpanel, grid);
        const shownCount = () => grid.querySelectorAll('[data-grid-item]').length;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        try {
          const room = Math.min(this.batchSize, this.maxCards - shownCount());
          while (state.buffer.length < room && state.nextPage <= state.pages) {
            await this.fetchPage(subpanel.dataset.collectionUrl, state);
          }
          state.buffer.splice(0, room).forEach((card) => grid.appendChild(card));
        } catch (error) {
          console.error(error);
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }

        const exhausted = state.buffer.length === 0 && state.nextPage > state.pages;
        if (exhausted || shownCount() >= this.maxCards) {
          button.hidden = true;
        }
      }

      async fetchPage(collectionUrl, state) {
        const url = new URL(collectionUrl, window.location.origin);
        url.searchParams.set('section_id', CARDS_SECTION_ID);
        url.searchParams.set('filter.v.availability', '1');
        url.searchParams.set('page', state.nextPage);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Related products request failed: ${response.status}`);
        }

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const wrapper = doc.querySelector('[data-related-cards]');

        state.pages = wrapper ? Number(wrapper.dataset.pages) || 0 : 0;
        state.nextPage += 1;
        if (!wrapper) return;

        wrapper.querySelectorAll('[data-related-card]').forEach((holder) => {
          const id = holder.dataset.productId;
          const card = holder.querySelector('[data-grid-item]');
          if (!card || id === this.productId || state.seen.has(id)) return;
          state.seen.add(id);
          state.buffer.push(card);
        });
      }
    }
  );
}

if (!customElements.get('related-tabs')) {
  customElements.define(
    'related-tabs',
    class extends HTMLElement {
      connectedCallback() {
        // theme:tab:change bubbles from the theme's <tabs-component> on facet switches.
        this.addEventListener('theme:tab:change', () => this.saveSelection());
        this.addEventListener('related-tabs:subtab-change', () => this.saveSelection());
        this.restoreSelection();
      }

      saveSelection() {
        const content = this.querySelector('.tab-content.current[data-facet]');
        if (!content) return;

        const subpanel = content.querySelector('[data-subpanel].current');
        writeRelatedTabsCookie(content.dataset.facet, subpanel?.dataset.subtabKey);
      }

      // Every step is optional: a facet or value this product doesn't have simply leaves
      // the default selection in place.
      async restoreSelection() {
        const saved = readRelatedTabsCookie();
        if (!saved) return;

        const facet = CSS.escape(saved.facet);
        const link = this.querySelector(`[data-tab][data-facet="${facet}"]`);
        const content = this.querySelector(`.tab-content[data-facet="${facet}"]`);
        if (!link || !content) return;

        if (typeof saved.subtab === 'string') {
          const trigger = content.querySelector(`[data-subtab][data-subtab-key="${CSS.escape(saved.subtab)}"]`);
          const panel = trigger?.closest('related-tabs-panel');
          if (trigger && typeof panel?.selectSubtab === 'function') {
            panel.selectSubtab(trigger, false);
          }
        }

        if (link.classList.contains('current')) return;

        await customElements.whenDefined('tabs-component');
        const tabs = link.closest('tabs-component');
        if (typeof tabs?.tabChange === 'function') {
          tabs.tabChange(link, content);
        } else {
          this.querySelectorAll('[data-tab].current, .tab-content.current').forEach((el) => el.classList.remove('current'));
          link.classList.add('current');
          content.classList.add('current');
        }
      }
    }
  );
}
